import React from 'react';
import { Calculator, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров анализа ликвидности
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  industryTypes
}) => {
  // Расчет текущих сумм активов и обязательств
  const totalCurrentAssets = Object.values(formData.currentAssets)
    .reduce((sum, value) => sum + (parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0), 0);
  
  const totalCurrentLiabilities = Object.values(formData.currentLiabilities)
    .reduce((sum, value) => sum + (parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0), 0);
  
  // Оценка текущей ликвидности
  const currentLiquidityRatio = totalCurrentLiabilities > 0 ? totalCurrentAssets / totalCurrentLiabilities : 0;
  
  // Обработчик изменения отрасли
  const handleIndustryChange = (e) => {
    const selectedIndustry = e.target.value;
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        industry: selectedIndustry
      }
    }));
  };

  // Обработчик быстрого изменения суммы оборотных активов
  const handleQuickAssetAdjustment = (percentage) => {
    const factor = 1 + (percentage / 100);
    const newAssets = {};
    
    // Применяем изменение ко всем активам
    Object.entries(formData.currentAssets).forEach(([key, value]) => {
      const numValue = parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0;
      newAssets[key] = Math.round(numValue * factor).toString();
    });
    
    setFormData(prev => ({
      ...prev,
      currentAssets: newAssets
    }));
  };

  // Обработчик быстрого изменения суммы краткосрочных обязательств
  const handleQuickLiabilityAdjustment = (percentage) => {
    const factor = 1 + (percentage / 100);
    const newLiabilities = {};
    
    // Применяем изменение ко всем обязательствам
    Object.entries(formData.currentLiabilities).forEach(([key, value]) => {
      const numValue = parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0;
      newLiabilities[key] = Math.round(numValue * factor).toString();
    });
    
    setFormData(prev => ({
      ...prev,
      currentLiabilities: newLiabilities
    }));
  };

  // Расчет
  const calculateLiquidity = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Текущее значение коэффициента ликвидности */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Текущий коэфф. ликвидности</label>
            <span className={`font-medium ${
              currentLiquidityRatio >= 2 ? "text-green-600 dark:text-green-400" :
              currentLiquidityRatio >= 1 ? "text-yellow-600 dark:text-yellow-400" :
              "text-red-600 dark:text-red-400"
            }`}>
              {formatNumber(currentLiquidityRatio, 2)}
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                currentLiquidityRatio >= 2 ? "bg-green-500" :
                currentLiquidityRatio >= 1 ? "bg-yellow-500" :
                "bg-red-500"
              }`} 
              style={{ width: `${Math.min(currentLiquidityRatio * 33, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>0</span>
            <span>1.0</span>
            <span>2.0</span>
            <span>3.0</span>
          </div>
        </div>
        
        {/* Выбор отрасли */}
        <div>
          <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Отрасль
          </label>
          <select
            id="industry"
            value={formData.settings.industry}
            onChange={handleIndustryChange}
            className="form-select w-full"
          >
            {industryTypes.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Быстрая настройка активов */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Оборотные активы</span>
            <span className="text-gray-600 dark:text-gray-400">
              {totalCurrentAssets.toLocaleString('ru-RU')} ₽
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1">
            <button
              type="button"
              onClick={() => handleQuickAssetAdjustment(-10)}
              className="px-2 py-1 text-xs rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
            >
              -10%
            </button>
            <button
              type="button"
              onClick={() => handleQuickAssetAdjustment(-5)}
              className="px-2 py-1 text-xs rounded border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300 dark:hover:bg-orange-900/30"
            >
              -5%
            </button>
            <button
              type="button"
              onClick={() => handleQuickAssetAdjustment(5)}
              className="px-2 py-1 text-xs rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/30"
            >
              +5%
            </button>
            <button
              type="button"
              onClick={() => handleQuickAssetAdjustment(10)}
              className="px-2 py-1 text-xs rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
            >
              +10%
            </button>
          </div>
        </div>
        
        {/* Быстрая настройка обязательств */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Краткосрочные обязательства</span>
            <span className="text-gray-600 dark:text-gray-400">
              {totalCurrentLiabilities.toLocaleString('ru-RU')} ₽
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1">
            <button
              type="button"
              onClick={() => handleQuickLiabilityAdjustment(-10)}
              className="px-2 py-1 text-xs rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/30"
            >
              -10%
            </button>
            <button
              type="button"
              onClick={() => handleQuickLiabilityAdjustment(-5)}
              className="px-2 py-1 text-xs rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
            >
              -5%
            </button>
            <button
              type="button"
              onClick={() => handleQuickLiabilityAdjustment(5)}
              className="px-2 py-1 text-xs rounded border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300 dark:hover:bg-orange-900/30"
            >
              +5%
            </button>
            <button
              type="button"
              onClick={() => handleQuickLiabilityAdjustment(10)}
              className="px-2 py-1 text-xs rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
            >
              +10%
            </button>
          </div>
        </div>
        
        {/* Детализация */}
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={formData.settings.includeDetailedAssets}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                settings: { ...prev.settings, includeDetailedAssets: e.target.checked }
              }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Детализировать активы
            </span>
          </label>
          
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={formData.settings.includeDetailedLiabilities}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                settings: { ...prev.settings, includeDetailedLiabilities: e.target.checked }
              }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Детализировать обязательства
            </span>
          </label>
          
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={formData.settings.includeBankruptcyRiskAssessment}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                settings: { ...prev.settings, includeBankruptcyRiskAssessment: e.target.checked }
              }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Оценка риска банкротства
            </span>
          </label>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateLiquidity}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || totalCurrentLiabilities <= 0}
          isLoading={isCalculating}
        >
          Рассчитать коэффициенты
        </Button>
        
        {totalCurrentLiabilities <= 0 && (
          <p className="text-xs text-red-600 dark:text-red-400 text-center">
            Краткосрочные обязательства должны быть больше нуля для расчета
          </p>
        )}
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;